import React, { useState, useEffect } from 'react';
import Typography from '@digital-hig/mui/dist/Typography';
import PropTypes from 'prop-types';
import styled from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import Grid from '@mui/material/Grid';
// eslint-disable-next-line import/no-extraneous-dependencies
import Button from '@mui/material/Button';
// eslint-disable-next-line import/no-extraneous-dependencies
import SvgIcon from '@mui/material/SvgIcon';
import ListIcon from '@digital-hig/icon/lib/esm/icons/list';
import BackIcon from '@digital-hig/icon/lib/esm/icons/caret-left';
import ForwardIcon from '@digital-hig/icon/lib/esm/icons/caret-right';
// eslint-disable-next-line import/no-extraneous-dependencies
import MUILink from '@mui/material/Link';
// eslint-disable-next-line import/no-extraneous-dependencies
import Divider from '@mui/material/Divider';
import { Link } from 'react-router-dom';
import UnitDrawer from './unitDrawer';
import { getUnitsAndModulesInCourse } from './utilities';
import {
  PAGETYPE_MODULE_UNIT, PAGETYPE_COURSE_UNIT, PAGETYPE_TUTORIAL, PAGETYPE_COURSE_MODULE,
} from './constants';

const UnitNavigation = ({
  data, tutorials, unitLookup, learningRecords, unitId, tutorialId, pageType, position,
}) => {
  let orderedIds = [];
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [previousBtn, setPreviousBtn] = useState({ label: '', link: '' });
  const [nextBtn, setNextBtn] = useState({ label: '', link: '' });

  if ((pageType === PAGETYPE_COURSE_UNIT || pageType === PAGETYPE_COURSE_MODULE) && tutorials?.length) {
    orderedIds = getUnitsAndModulesInCourse({ tutorials });
  } else if (pageType === PAGETYPE_MODULE_UNIT && data?.units) {
    orderedIds = data.units;
  } else if (pageType === PAGETYPE_TUTORIAL && tutorials?.length) {
    orderedIds = tutorials.map((tutorial) => ({ id: tutorial.id }));
  }
  // all ordered course or module or unit ids
  const orderedCMUIds = orderedIds && orderedIds.map((cmu) => cmu.id);

  const findPrevUnitId = (uid) => {
    if (!uid) {
      const prevTutorialIdx = tutorials.findIndex((tutorial) => tutorial.id === tutorialId) - 1;
      const prevTutorialLastUnit = tutorials[prevTutorialIdx]?.units[tutorials[prevTutorialIdx].units.length - 1];

      return prevTutorialLastUnit ? prevTutorialLastUnit.id : -1;
    }

    return uid;
  };

  let nextPosition;
  let prevPosition;

  if (pageType === PAGETYPE_COURSE_UNIT || pageType === PAGETYPE_MODULE_UNIT || pageType === PAGETYPE_COURSE_MODULE) {
    // as unit id will not be available when user land on module,
    // filtering from all tutorials based on current tutorialId and taking first unit as nextUnit.
    const nextUnitId = unitId || tutorials.filter((tutorial) => tutorial.id === tutorialId)[0].units[0].id;
    const prevUnitId = findPrevUnitId(unitId);
    const nextUnitIndex = Math.max(0, orderedCMUIds?.indexOf(nextUnitId));
    const prevUnitIndex = prevUnitId !== -1 ? Math.max(0, orderedCMUIds?.indexOf(prevUnitId)) : prevUnitId;
    nextPosition = unitId ? nextUnitIndex + 1 : nextUnitIndex;
    prevPosition = unitId ? prevUnitIndex - 1 : prevUnitIndex;
  }

  if (pageType === PAGETYPE_TUTORIAL) {
    const idxOfCurTutorial = tutorials.findIndex((tutorial) => tutorial.id === tutorialId);
    nextPosition = idxOfCurTutorial + 1;
    prevPosition = idxOfCurTutorial - 1;
  }

  const onCourseOutline = () => {
    setIsDrawerOpen(true);
  };

  // when hitting new location, drawer should be closed
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [unitId, tutorialId]);

  // for previous button
  useEffect(() => {
    let prevUrl;
    let prevText;
    if (prevPosition >= 0) {
      const isPrevModule = Object.hasOwnProperty.call(orderedIds[prevPosition], 'units');
      // prevUrl = isPrevModule ? `${data.uri}/module/${orderedIds[prevPosition].id}` : `${data.uri}/unit/${orderedIds[prevPosition].id}`;
      prevText = isPrevModule ? 'Module overview' : 'Previous unit';
      if (isPrevModule) {
        prevText = 'Module overview';
        prevUrl = `${data.uri}/module/${orderedIds[prevPosition].id}`;
      } else if (pageType === PAGETYPE_MODULE_UNIT || pageType === PAGETYPE_COURSE_UNIT || pageType === PAGETYPE_COURSE_MODULE) {
        prevText = 'Previous unit';
        prevUrl = `${data.uri}/unit/${orderedIds[prevPosition].id}`;
      } else {
        prevText = 'Previous tutorial';
        prevUrl = `${data.uri}/${orderedIds[prevPosition].id}`;
      }
    } else { // Course Overview
      prevUrl = data.uri;
      if (pageType === PAGETYPE_MODULE_UNIT) {
        prevText = 'Module overview';
      } else if (pageType === PAGETYPE_COURSE_UNIT || pageType === PAGETYPE_COURSE_MODULE) {
        prevText = 'Course overview';
      } else {
        prevText = 'Curated list';
      }
    }
    setPreviousBtn({ label: prevText, link: prevUrl });
  }, [unitId, tutorialId]);

  // for next button
  useEffect(() => {
    let nextUrl;
    let nextText;
    if (orderedCMUIds?.length > nextPosition) { // Next Item
      const isNextModule = Object.hasOwnProperty.call(orderedIds[nextPosition], 'units');
      nextText = isNextModule ? 'Next module' : 'Next unit';
      if (isNextModule) {
        nextText = 'Next module';
        nextUrl = `${data.uri}/module/${orderedIds[nextPosition].id}`;
      } else if (pageType === PAGETYPE_MODULE_UNIT || pageType === PAGETYPE_COURSE_UNIT || pageType === PAGETYPE_COURSE_MODULE) {
        nextText = 'Next unit';
        nextUrl = `${data.uri}/unit/${orderedIds[nextPosition].id}`;
      } else {
        nextText = 'Next tutorial';
        nextUrl = `${data.uri}/${orderedIds[nextPosition].id}`;
      }
      // nextUrl = isNextModule ? `${data.uri}/module/${orderedIds[nextPosition].id}` : `${data.uri}/unit/${orderedIds[nextPosition].id}`;
    } else { // Course Overview
      nextUrl = data.uri;
      if (pageType === PAGETYPE_MODULE_UNIT) {
        nextText = 'Return to module overview';
      } else if (pageType === PAGETYPE_COURSE_UNIT || pageType === PAGETYPE_COURSE_MODULE) {
        nextText = 'Return to course overview';
      } else {
        nextText = 'Return to curated list';
      }
    }
    setNextBtn({ label: nextText, link: nextUrl });
  }, [unitId, tutorialId]);

  return (
    <RootElement className={`${position}-pagination`}>
      <Grid className="unit-pagination-container maxWidth">
        <Grid component="ul" className="unit-navigation">
          <Grid component="li">
            <Button
              onClick={onCourseOutline}
              startIcon={<SvgIcon size="medium"><ListIcon /></SvgIcon>}
              variant="link-button"
              className="list-btn"
            >
              {pageType === PAGETYPE_MODULE_UNIT && 'Module outline'}
              {(pageType === PAGETYPE_COURSE_UNIT || pageType === PAGETYPE_COURSE_MODULE) && 'Course outline'}
              {pageType === PAGETYPE_TUTORIAL && 'Curated list'}
            </Button>
          </Grid>
          <Grid component="li" sx={{ display: { sm: 'none', md: 'block' } }}>
            <Typography>
              <SvgIcon size="medium"><BackIcon /></SvgIcon>
              {' '}
              <MUILink underline="none" component={Link} to={previousBtn.link} className="unit-link">{previousBtn.label}</MUILink>
            </Typography>
          </Grid>
          <Divider flexItem orientation="vertical" sx={{ mx: 4, display: { sm: 'none', md: 'block' } }} />
          <Grid component="li">
            <Typography>
              <MUILink underline="none" component={Link} to={nextBtn.link} className="unit-link">{nextBtn.label}</MUILink>
              {' '}
              <SvgIcon size="medium"><ForwardIcon /></SvgIcon>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      {isDrawerOpen && (
        <UnitDrawer
          pageType={pageType}
          curUnitId={unitId}
          tutorialId={tutorialId}
          isOpen={isDrawerOpen}
          openCloseCallback={setIsDrawerOpen}
          data={data}
          tutorials={tutorials}
          unitLookup={unitLookup}
          learningRecords={learningRecords}
        />
      )}

    </RootElement>
  );
};

const RootElement = styled.div`
    position: sticky;
    top: 0;
    z-index: 99;
    background: #fff;
    border-bottom: 1px solid rgb(229, 229, 229);
.unit-pagination-container {
    margin: 0 auto;
    box-sizing: content-box;
    padding-top: 15px;
    padding-bottom: 15px;

ul.unit-navigation {
  display: flex;
  align-items: center;
  list-style-type: none;
  padding: 0;
  margin: 0;
 
  li {
    &:first-child {
      flex: 1;
    }
    a {
      font-weight: 600;
    }
  }
}
.list-btn{
  text-transform: none;
  font-weight: 600;
  font-size: 16px;
  margin-left: -2px;
  align-items: center;
  &:hover {
    background: none;
  }
 }
}
@media only screen and (max-width: 560px) {
  padding-left: 16px;
  padding-right: 16px;

  &.top-pagination {
    padding: 16px;
  }
  
  .unit-pagination-container {
    padding: 0;
  }
}
`;

UnitNavigation.propTypes = {
  pageType: PropTypes.string,
  data: PropTypes.shape({
    uri: PropTypes.string,
    units: PropTypes.arrayOf({}),
  }),
  tutorials: PropTypes.arrayOf({}),
  unitLookup: PropTypes.shape({}),
  learningRecords: PropTypes.shape({}),
  unitId: PropTypes.string,
  tutorialId: PropTypes.string,
  curTutorial: PropTypes.shape({}),
  position: PropTypes.string,
};

UnitNavigation.defaultProps = {
  pageType: '',
  data: {},
  tutorials: [],
  unitLookup: {},
  learningRecords: {},
  unitId: '',
  tutorialId: '',
  curTutorial: undefined,
  position: '',
};

export default React.memo(UnitNavigation);
